import type { ShiftView } from "@kopi/contracts";
import type { CashMovementKind } from "@kopi/contracts/codes";
import { Button } from "@kopi/ui/components/button";
import { Skeleton } from "@kopi/ui/components/skeleton";
import { useQuery, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { CheckCheckIcon, ClockIcon, LockIcon } from "lucide-react";
import { type ReactNode, useState } from "react";
import { Page, PageHeader } from "@/components/page-header";
import {
  ApproveShiftDialog,
  CashBreakdownList,
  CloseShiftDialog,
  KIND_ICONS,
  KIND_KEYS,
  MovementDialog,
  MovementList,
  OpenShiftDialog,
  PendingQrisList,
  ShiftFigures,
  ShiftStatusBadge,
} from "@/components/shifts";
import { shifts } from "@/i18n/messages/shifts";
import { useMessages } from "@/i18n/provider";
import { canAt } from "@/lib/access";
import { currentShiftQuery, meScopesQuery } from "@/lib/queries";

export const Route = createFileRoute("/_authed/shift")({
  staticData: { section: "workspace", crumb: "nav.shift" },
  component: ShiftPage,
});

function Notice(props: { title: string; children: ReactNode; action?: ReactNode }) {
  const { t } = useMessages(shifts);
  return (
    <Page width="narrow">
      <PageHeader title={t("shifts.title")} subtitle={t("shifts.subtitle")} />
      <section className="flex flex-col items-start gap-3 rounded-xl border border-dashed bg-card p-6">
        <h2 className="font-semibold">{props.title}</h2>
        <div className="text-muted-foreground text-sm">{props.children}</div>
        {props.action}
      </section>
    </Page>
  );
}

/** The register's cash shift (R1-15): open it, record cash in and out, close it with a blind count. */
function ShiftPage() {
  const { t } = useMessages(shifts);
  const { data: scopes } = useSuspenseQuery(meScopesQuery);
  const device = scopes.device;
  const allowed = Boolean(device && canAt(scopes, "cash.shift.operate", device.outletId));
  const current = useQuery({ ...currentShiftQuery, enabled: allowed });
  const [opening, setOpening] = useState(false);

  if (!device) return <Notice title={t("shifts.needRegister")}>{t("shifts.needRegisterHint")}</Notice>;
  if (!allowed) {
    return (
      <Notice title={t("shifts.notAllowed", { outlet: device.outletName })}>
        {t("shifts.notAllowedHint")}
      </Notice>
    );
  }
  if (current.isPending) {
    return (
      <Page>
        <PageHeader title={t("shifts.title")} subtitle={t("shifts.subtitle")} />
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4" aria-busy="true">
          {["a", "b", "c", "d"].map((key) => (
            <Skeleton key={key} className="h-32 rounded-xl" />
          ))}
        </div>
      </Page>
    );
  }
  if (current.isError) {
    return (
      <Notice
        title={t("shifts.loadFailed")}
        action={
          <Button type="button" variant="outline" onClick={() => void current.refetch()}>
            {t("common.retry")}
          </Button>
        }
      >
        {null}
      </Notice>
    );
  }

  const { shift, lastNumber } = current.data;
  if (!shift) {
    return (
      <>
        <Notice
          title={t("shifts.none", { register: device.registerName })}
          action={
            <Button type="button" size="lg" onClick={() => setOpening(true)}>
              <ClockIcon />
              {t("shifts.open")}
            </Button>
          }
        >
          {t("shifts.noneHint")}
        </Notice>
        {opening && (
          <OpenShiftDialog
            outletId={device.outletId}
            registerName={device.registerName}
            lastNumber={lastNumber}
            onClose={() => setOpening(false)}
          />
        )}
      </>
    );
  }
  return <OpenShift shift={shift} mayApprove={canAt(scopes, "cash.shift.approve", shift.outletId)} />;
}

function OpenShift(props: { shift: ShiftView; mayApprove: boolean }) {
  const { shift, mayApprove } = props;
  const { t, format } = useMessages(shifts);
  const [dialog, setDialog] = useState<CashMovementKind | "close" | "approve" | null>(null);
  const close = () => setDialog(null);
  const isOpen = shift.status === "open";

  return (
    <Page>
      <PageHeader
        eyebrow={t("shifts.ofRegister", { register: shift.registerName, number: shift.number })}
        title={t("shifts.title")}
        subtitle={t("shifts.openedBy", { time: format.dateTime(shift.openedAt), name: shift.openedBy })}
        actions={
          <>
            <ShiftStatusBadge status={shift.status} />
            {!isOpen && mayApprove && (
              <Button type="button" onClick={() => setDialog("approve")}>
                <CheckCheckIcon />
                {t("shifts.approve")}
              </Button>
            )}
          </>
        }
      />
      {isOpen ? (
        <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap">
          {(["paid_in", "paid_out", "drop"] as const).map((kind) => (
            <Button key={kind} type="button" variant="outline" size="lg" onClick={() => setDialog(kind)}>
              {KIND_ICONS[kind]}
              {t(KIND_KEYS[kind])}
            </Button>
          ))}
          <Button type="button" size="lg" onClick={() => setDialog("close")}>
            <LockIcon />
            {t("shifts.close")}
          </Button>
        </div>
      ) : (
        <section role="status" className="flex flex-col gap-1 rounded-xl border bg-card p-4">
          <h2 className="font-semibold">{t("shifts.awaiting")}</h2>
          <p className="text-muted-foreground text-sm">{t("shifts.awaitingHint")}</p>
        </section>
      )}
      <ShiftFigures shift={shift} />
      <CashBreakdownList shift={shift} />
      <PendingQrisList shift={shift} />
      <section className="flex flex-col gap-3">
        <h2 className="font-semibold text-lg">{t("shifts.movements")}</h2>
        <MovementList shift={shift} reviewable={mayApprove} />
      </section>
      {dialog === "close" && <CloseShiftDialog shift={shift} onClose={close} />}
      {dialog === "approve" && <ApproveShiftDialog shift={shift} onClose={close} />}
      {dialog && dialog !== "close" && dialog !== "approve" && (
        <MovementDialog shift={shift} kind={dialog} onClose={close} />
      )}
    </Page>
  );
}
